import React, { useCallback, useEffect, useState } from "react";
import { apiCall } from "../service/api-service";

// Stored AI chat history with its token and cost totals. Reads crm_ai_chats,
// one row per exchange, newest first.
const AiUsage = () => {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const response = await apiCall(
      "GET",
      `web/ai_usage?from_date=${fromDate}&to_date=${toDate}`,
    );
    if (response.data?.success === true) {
      setRows(response.data.rows ?? []);
      setTotals(response.data.totals ?? {});
    } else {
      setError(response.data?.message || "Could not load AI usage");
    }
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const money = (v) =>
    v === null || v === undefined ? "—" : `$${Number(v).toFixed(4)}`;
  const num = (v) => Number(v ?? 0).toLocaleString();

  return (
    <div style={{ padding: 24 }}>
      <div style={titleRow}>
        <h4 style={{ color: "#172B4D", margin: 0 }}>AI Usage</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={input}
          />
          <span style={{ color: "#7A869A" }}>to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={input}
          />
        </div>
      </div>

      <div style={cards}>
        <Stat label="Questions" value={num(totals.exchanges)} />
        <Stat label="Conversations" value={num(totals.sessions)} />
        <Stat label="API calls" value={num(totals.api_calls)} />
        <Stat label="Input tokens" value={num(totals.input_tokens)} />
        <Stat label="Output tokens" value={num(totals.output_tokens)} />
        <Stat
          label="Cost"
          value={money(totals.cost_usd)}
          // A model with no entry in the rate table stores NULL rather than 0,
          // so say how many rows the total is missing instead of quietly
          // under-reporting.
          note={
            Number(totals.unpriced) > 0
              ? `${num(totals.unpriced)} unpriced`
              : null
          }
        />
      </div>

      {error && <div style={{ color: "#CC3333", marginTop: 16 }}>{error}</div>}
      {loading && <div style={{ color: "#7A869A", marginTop: 16 }}>Loading…</div>}

      {!loading && !error && rows.length === 0 && (
        <div style={{ color: "#7A869A", marginTop: 16 }}>
          No AI conversations in this date range.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div style={tableWrap}>
          <table style={table}>
            {/* Fixed layout plus explicit widths: without it the Question
                column sizes to its content and shoves the numeric columns
                out of the viewport. */}
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    // Header alignment must match the cell's, or numbers and
                    // their labels sit at opposite ends of the column.
                    style={{ ...th, textAlign: c.num ? "right" : "left" }}
                  >
                    {c.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={td}>{r.asked_at}</td>
                    <td style={td}>{r.asked_by || "—"}</td>
                    <td style={td}>{r.module}</td>
                    <td style={td} title={r.model}>
                      {r.model}
                    </td>
                    {/* Truncated rather than wrapped, so every row stays one
                        line and the columns line up down the page. The full
                        text is on hover, and in the expanded row below. */}
                    <td style={td} title={r.question}>
                      {r.question}
                    </td>
                    <td style={tdNum}>{r.api_calls}</td>
                    <td style={tdNum}>{num(r.input_tokens)}</td>
                    <td style={tdNum}>{num(r.output_tokens)}</td>
                    <td style={tdNum}>{money(r.cost_usd)}</td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={9} style={replyCell}>
                        {r.reply}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {rows.length >= 500 && (
            <div style={{ color: "#7A869A", fontSize: 12, padding: 8 }}>
              Showing the most recent 500 exchanges. The totals above cover the
              whole range.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, note }) => (
  <div style={card}>
    <div style={{ color: "#7A869A", fontSize: 12 }}>{label}</div>
    <div style={{ color: "#172B4D", fontSize: 20, fontWeight: 600 }}>{value}</div>
    {note && <div style={{ color: "#C58A00", fontSize: 11 }}>{note}</div>}
  </div>
);

const titleRow = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 16,
};

const input = {
  border: "1px solid #EBEBEB",
  borderRadius: 8,
  fontSize: 13,
  padding: "6px 10px",
};

const cards = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
};

const card = {
  background: "#FFFFFF",
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  minWidth: 130,
  padding: "12px 16px",
};

const tableWrap = {
  background: "#FFFFFF",
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  marginTop: 16,
  overflowX: "auto",
};

// One definition per column: the width, and whether it is numeric. Header and
// cell both read from it, so their alignment cannot drift apart.
const COLUMNS = [
  { key: "When", width: 120 },
  { key: "Who", width: 110 },
  { key: "Module", width: 90 },
  { key: "Model", width: 150 },
  { key: "Question", width: "auto" },
  { key: "Calls", width: 64, num: true },
  { key: "In", width: 84, num: true },
  { key: "Out", width: 84, num: true },
  { key: "Cost", width: 96, num: true },
];

const table = {
  borderCollapse: "collapse",
  fontSize: 13,
  tableLayout: "fixed",
  width: "100%",
  minWidth: 900,
};

const th = {
  borderBottom: "1px solid #EBEBEB",
  color: "#7A869A",
  fontWeight: 500,
  padding: "10px 12px",
  whiteSpace: "nowrap",
};

const td = {
  borderBottom: "1px solid #F4F5F7",
  color: "#172B4D",
  padding: "10px 12px",
  // Every row stays exactly one line tall; a long question ellipses instead
  // of wrapping and knocking the row below out of line.
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const tdNum = {
  ...td,
  textAlign: "right",
  // Digits share a width, so columns of numbers line up on the decimal.
  fontVariantNumeric: "tabular-nums",
};

const replyCell = {
  background: "#F4F5F7",
  borderBottom: "1px solid #EBEBEB",
  color: "#172B4D",
  fontSize: 13,
  lineHeight: 1.6,
  padding: "12px 16px",
  whiteSpace: "pre-wrap",
};

export default AiUsage;
